import {
    useState,
    useEffect,
    useContext,
    useCallback,
    SyntheticEvent,
} from 'react';
import MaskedTextInput from 'react-text-mask';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './quiz-date-of-birth-field.module.scss';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import {
    faCalendar,
    faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { getTodayDate } from 'src/helpers/date';

const MIN_DATE = '1900-01-01';
const MAX_DATE = getTodayDate();

const QuizDateOfBirthField = () => {
    const { currentQuestion, handleSubmitAnswer, state } =
        useContext(QuizContext);
    const {
        questionId,
        questionText,
        answerList,
        userAnswers,
        prefillAnswers,
        questionImage,
    } = currentQuestion || {};
    const { frontendStamp: answerId } = answerList[0] || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [dateValue, setDateValue] = useState<Date | null>(null);
    const [isError, setIsError] = useState(false);
    const [inputFocused, setInputFocused] = useState(false);
    const handleChange = (date: Date | null, event?: SyntheticEvent) => {
        if (event) event.preventDefault();
        setDateValue(date);
    };

    // Set existing answer
    useEffect(() => {
        let newAnswerText: string | null = null;
        if (userAnswers.length > 0) {
            newAnswerText = userAnswers[0].answerText;
        } else if (prefillAnswers.length > 0) {
            newAnswerText = prefillAnswers[0].answerText;
        }
        if (newAnswerText) {
            const newAnswerDate = new Date(newAnswerText);
            // Check if date is valid in case backend sends us something weird
            setDateValue(isNaN(newAnswerDate.getTime()) ? null : newAnswerDate);
        }
    }, [userAnswers, prefillAnswers]);

    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (dateValue && !continueDisabled && questionId) {
            const timezoneOffset = dateValue.getTimezoneOffset() * 60000;
            // Convert to ISO string and remove timezone offset to get date only
            const dateText = new Date(dateValue.getTime() - timezoneOffset)
                .toISOString()
                .split('T')[0];
            setIsError(false);
            handleSubmitAnswer({
                questionId: questionId,
                questionText: questionText,
                answerIds: answerId ? [answerId] : undefined,
                answerTexts: [dateText],
            });
        } else {
            setIsError(true);
        }
    }, [
        continueDisabled,
        dateValue,
        questionId,
        questionText,
        answerId,
        handleSubmitAnswer,
    ]);

    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !inputFocused) handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit, inputFocused]);

    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueDisabled(dateValue === null);
        }
    }, [dateValue, pageTransitioning, setContinueDisabled]);

    return (
        <div className={styles['quiz-date-of-birth-field--wrapper']}>
            {questionImage ? (
                <img
                    className="h-16 w-16"
                    src={questionImage}
                    alt={questionText}
                />
            ) : null}
            <div className={styles['quiz-field-header']}>
                <h4 className={styles['title']}>{questionText}</h4>
            </div>
            <div className={styles['input--wrapper']}>
                <label className="p2 highlight">Date of birth</label>
                <div className="relative flex items-center">
                    <DatePicker
                        className={classNames(styles['default-field'], {
                            [styles['error-field']]: isError,
                        })}
                        minDate={new Date(MIN_DATE)}
                        maxDate={new Date(MAX_DATE)}
                        selected={dateValue}
                        onChange={handleChange}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        dateFormat="dd/MM/yyyy"
                        placeholderText="DD/MM/YYYY"
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => setInputFocused(false)}
                        customInput={
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            <MaskedTextInput
                                data-testid="date-of-birth-input"
                                mask={[
                                    /\d/,
                                    /\d/,
                                    '/',
                                    /\d/,
                                    /\d/,
                                    '/',
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                ]}
                            />
                        }
                    />
                    <FontAwesomeIcon
                        icon={faCalendar}
                        className="absolute right-3 text-grey-300"
                    />
                </div>
                {isError && (
                    <p className={classNames(styles['error-message'])}>
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        This field is required
                    </p>
                )}
            </div>
        </div>
    );
};

export default QuizDateOfBirthField;
